import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { DatePicker } from '@fikasio/react-ui-components';
import {
  useAddObjectiveEntryMutation,
  useDeleteObjectiveEntryMutation,
  useGetObjectiveEntriesQuery,
  usePatchObjectiveEntryMutation,
} from 'services/objectives/api';
import { Objective, ObjectiveEntry } from 'services/objectives/types';
import { toDateTime } from 'utils/date';
import ObjectiveUnitAffix from '../ObjectiveUnitAffix';
import { formatDuration, formatObjectiveQuantity, isDurationFormat, parseDuration } from '../formatUnit';
import { goalKindOf } from '../ObjectivesViewer/utils';
import ObjectiveProgressChart from './ObjectiveProgressChart';
import { compareTargets, formatTrackNumber } from './targetComparison';
import styles from './ObjectiveProgress.module.css';
import 'components/UI/Form.css';

interface ObjectiveProgressProps {
  objective: Objective;
  stats?: React.ReactNode;
}

const now = () => new Date();

const ObjectiveProgress = ({
  objective,
  stats,
}: ObjectiveProgressProps) => {
  const { t } = useTranslation();
  const { data: entries = [] } = useGetObjectiveEntriesQuery(objective.id);
  const [addEntry] = useAddObjectiveEntryMutation();
  const [patchEntry] = usePatchObjectiveEntryMutation();
  const [deleteEntry] = useDeleteObjectiveEntryMutation();
  const [value, setValue] = useState('');
  const [recordedAt, setRecordedAt] = useState<Date | null>(now);
  const [datePickerKey, setDatePickerKey] = useState(0);
  const [note, setNote] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const kind = goalKindOf(objective);
  const comparisons = compareTargets({
    entries,
    goalKind: kind,
    targets: objective.targets,
  });

  const resetForm = () => {
    setValue('');
    setNote('');
    setRecordedAt(now());
    setDatePickerKey(key => key + 1);
    setEditingId(null);
    setError(null);
  };

  const duration = isDurationFormat(objective);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = duration ? parseDuration(value) : Number(value);
    const recordedAtIso = recordedAt
      ? DateTime.fromJSDate(recordedAt).set({ millisecond: 0 }).toISO()
      : null;
    if (value.trim() === '' || !recordedAtIso) {
      setError(t('required'));
      return;
    }
    if (parsed == null || Number.isNaN(parsed)) {
      setError(t(duration ? 'invalidDuration' : 'required'));
      return;
    }
    const body = {
      note: note.trim(),
      objectiveId: objective.id,
      recordedAt: recordedAtIso,
      value: parsed,
    };
    const result = editingId
      ? await patchEntry({ ...body, id: editingId })
      : await addEntry(body);
    if ('error' in result && result.error) {
      setError(t('invalidSubmission'));
      return;
    }
    resetForm();
  };

  const startEdit = (entry: ObjectiveEntry) => {
    setEditingId(entry.id);
    setValue(duration ? formatDuration(entry.value) : String(entry.value));
    setRecordedAt(DateTime.fromISO(entry.recordedAt).toJSDate());
    setDatePickerKey(key => key + 1);
    setNote(entry.note || '');
    setError(null);
  };

  return (
    <div className={styles.split}>
      <div className={styles.log}>
        {
          comparisons.length > 0 && (
            <ul className={styles.comparisons}>
              {
                comparisons.map(item => {
                  const expected = formatObjectiveQuantity(formatTrackNumber(item.expected), objective);
                  const text = item.relation === 'distance'
                    ? t('distanceFromTargetTrack', {
                      distance: formatObjectiveQuantity(formatTrackNumber(item.distance ?? 0), objective),
                      expected,
                      name: item.name,
                    })
                    : t(item.relation === 'ahead' ? 'aheadOfTarget' : 'behindTarget', {
                      expected,
                      name: item.name,
                    });
                  return <li key={item.id}>{text}</li>;
                })
              }
            </ul>
          )
        }
        <form onSubmit={onSubmit}>
          {error && <Alert variant="danger">{error}</Alert>}
          <div style={{ alignItems: 'flex-end', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <label className="form-label" htmlFor="objective-entry-value">{t('value')}</label>
              <ObjectiveUnitAffix quantity={objective}>
                <input
                  className="form-control"
                  id="objective-entry-value"
                  onChange={event => setValue(event.target.value)}
                  placeholder={duration ? t('durationPlaceholder') : undefined}
                  step={duration ? undefined : 'any'}
                  style={{ width: duration ? 148 : 120 }}
                  type={duration ? 'text' : 'number'}
                  value={value}
                />
              </ObjectiveUnitAffix>
            </div>
            <div className={styles.recordedAt}>
              <label className="form-label">{t('recordedOn')}</label>
              <br />
              <DatePicker
                className="form-control"
                defaultValue={recordedAt ?? undefined}
                displayFormat="yyyy-MM-dd HH:mm"
                key={datePickerKey}
                onChange={date => {
                  setRecordedAt(DateTime.fromJSDate(date).set({ millisecond: 0 }).toJSDate());
                }}
                showTimeSelect
                timeCaption={t('hour')}
                timeFormat="HH:mm"
                timeIntervals={15}
              />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <label className="form-label" htmlFor="objective-entry-note">{t('note')}</label>
              <input
                className="form-control"
                id="objective-entry-note"
                onChange={event => setNote(event.target.value)}
                value={note}
              />
            </div>
            <Button type="submit" variant="success">
              {editingId ? t('save') : t('logProgress')}
            </Button>
            {editingId && (
              <Button
                onClick={resetForm}
                type="button"
                variant="outline-secondary"
              >
                {t('cancel')}
              </Button>
            )}
          </div>
        </form>
        {
          entries.length === 0
            ? <p style={{ marginTop: 16 }}>{t('noProgressEntries')}</p>
            : (
              <table style={{ marginTop: 16, width: '100%' }}>
                <tbody>
                  {entries.map(entry => (
                    <tr key={entry.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {toDateTime(entry.recordedAt)?.toFormat('yyyy-MM-dd HH:mm')}
                      </td>
                      <td>{formatObjectiveQuantity(entry.value, objective)}</td>
                      <td>{entry.note}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Button
                          onClick={() => startEdit(entry)}
                          size="sm"
                          type="button"
                          variant="outline-secondary"
                        >
                          {t('edit')}
                        </Button>
                        {' '}
                        <Button
                          onClick={() => deleteEntry({ id: entry.id, objectiveId: objective.id })}
                          size="sm"
                          type="button"
                          variant="outline-danger"
                        >
                          {t('delete')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
        }
      </div>
      <div className={styles.aside}>
        <ObjectiveProgressChart entries={entries} objective={objective} />
        {stats}
      </div>
    </div>
  );
};

export default ObjectiveProgress;
